"use client";

import { Button } from "@/components/ui/button";
import DocumentUpload from "@/components/ui/document-upload";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AxiosError } from "axios";
import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { saveVehicle } from "../../../../services/clientAreaService";
import { consultarPlaca, jsonVeiculos } from "../../../../services/flowQuote";
import { Veiculo, VeiculoJson } from "../../../../types/Veiculo";
import { FilteredSelect } from "../../../ui/filtered-select";

type VehicleFormValues = {
  descricao: string;
  placa: string;
  anoFab: string;
  anoMod: string;
  cor: string;
  chassi: string;
};

type VehicleOption = {
  label: string;
  value: string;
};

type VehicleFormProps = {
  cliCodigo: string;
  initial?: Veiculo | null;
  onSaved: (v: Veiculo) => void;
  onCancel: () => void;
};

export default function VehicleForm({
  cliCodigo,
  initial,
  onSaved,
  onCancel,
}: VehicleFormProps) {
  const [isSearchingPlate, setIsSearchingPlate] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [vehicleOptions, setVehicleOptions] = useState<VehicleOption[]>([]);

  const form = useForm<VehicleFormValues>({
    defaultValues: {
      descricao: initial?.vmd_descricao ?? "",
      placa: initial?.cve_placa ?? "",
      anoFab: initial?.cve_ano_fab ?? "",
      anoMod: initial?.cve_ano_mod ?? "",
      cor: initial?.cor_descricao ?? "",
      chassi: initial?.cve_chassi ?? "",
    },
    mode: "onChange",
  });

  const chassi = useWatch({
    control: form.control,
    name: "chassi",
  });
  const watchedValues = useWatch({
    control: form.control,
  });

  useEffect(() => {
    setErrorMessage(null);
  }, [watchedValues]);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function fetchVehicles() {
      const data = await jsonVeiculos();
      setVehicleOptions(
        (data || []).map((v: VeiculoJson) => ({
          label: v?.vmd_descricao,
          value: v?.vmd_codigo,
        })),
      );
    }
    fetchVehicles();
  }, []);

  const handleSearchPlate = async () => {
    const plate = form.getValues("placa");
    const descricaoValue = form.getValues("descricao");

    if (!plate || plate.length < 7) return;

    setIsSearchingPlate(true);
    setErrorMessage(null);

    try {
      let cveCodigo = 0;
      if (descricaoValue) {
        const selectedVehicle = vehicleOptions.find(
          (opt) => opt.value === descricaoValue,
        );

        if (selectedVehicle) {
          cveCodigo = Number(selectedVehicle.value) || 0;
        }
      }

      const data = await consultarPlaca(plate, cveCodigo);

      if (data.marca && data.modelo) {
        const vehicleOption = vehicleOptions.find(
          (opt) =>
            opt.label.toLowerCase().includes(data.marca.toLowerCase()) &&
            opt.label.toLowerCase().includes(data.modelo.toLowerCase()),
        );
        if (vehicleOption) {
          form.setValue("descricao", vehicleOption.label);
        } else {
          form.setValue("descricao", `${data.marca} ${data.modelo}`);
        }
      } else {
        form.setValue("descricao", data.MODELO ?? "");
      }

      form.setValue("anoMod", data.anoModelo ?? "");
      form.setValue("cor", data.cor ?? "");
      form.setValue("chassi", data.chassi ?? "");
      form.clearErrors();
    } catch (error: unknown) {
      const msg =
        error instanceof AxiosError
          ? String(error.response?.data ?? "") ||
            "Não foi possível encontrar as informações do veículo."
          : "Não foi possível encontrar as informações do veículo.";
      setErrorMessage(
        `${msg} Continue o processo, nosso time irá cuidar disso...`,
      );
    } finally {
      setIsSearchingPlate(false);
    }
  };

  const handleSubmit: SubmitHandler<VehicleFormValues> = async (values) => {
    const v: Veiculo = {
      vmd_descricao: values.descricao,
      cve_placa: values.placa,
      cve_ano_fab: values.anoFab,
      cve_ano_mod: values.anoMod,
      cor_descricao: values.cor,
      cve_chassi: values.chassi,
    };

    if (values.descricao) {
      const selectedVehicle = vehicleOptions.find(
        (opt) => opt.value === values.descricao,
      );
      if (selectedVehicle) {
        v.vmd_codigo = selectedVehicle.value;
      }
    }

    if (values.cor) {
      v.cor_codigo = values.cor;
    }

    if (initial?.clv_codigo) {
      v.clv_codigo = initial.clv_codigo;
    }

    setSaving(true);
    try {
      await saveVehicle(cliCodigo, v);
      onSaved(v);
    } catch (error) {
      console.error("Erro ao salvar veículo:", error);
      setErrorMessage("Erro ao salvar veículo. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="descricao"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Descrição:</label>
              <FormControl>
                <FilteredSelect
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Marca e modelo do veículo"
                  options={vehicleOptions}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="placa"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Placa:</label>
              <FormControl>
                <div className="flex gap-1">
                  <Input
                    placeholder="ABC1D23"
                    className="rounded-r-sm"
                    {...field}
                  />
                  <Button
                    variant="border"
                    type="button"
                    onClick={handleSearchPlate}
                    size="input"
                    className="rounded-l-sm rounded-r-xl"
                    disabled={isSearchingPlate}
                  >
                    {isSearchingPlate ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Buscando...
                      </>
                    ) : (
                      <>
                        Buscar
                        <Search className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
              {errorMessage && (
                <p className="text-sm text-orange-600 mt-1">{errorMessage}</p>
              )}
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="anoFab"
            render={({ field }) => (
              <FormItem>
                <label className="block text-sm mb-1">Ano Fab.:</label>
                <FormControl>
                  <Input
                    placeholder="2021"
                    {...field}
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="anoMod"
            render={({ field }) => (
              <FormItem>
                <label className="block text-sm mb-1">Ano Mod.:</label>
                <FormControl>
                  <Input
                    placeholder="2022"
                    {...field}
                    disabled
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="cor"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Cor:</label>
              <FormControl>
                <Input
                  placeholder="Branco"
                  {...field}
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="chassi"
          render={({ field }) => (
            <FormItem>
              <label className="block text-sm mb-1">Chassi:</label>
              <FormControl>
                <Input
                  placeholder="CHASSI..."
                  {...field}
                  disabled
                  className="bg-gray-100 cursor-not-allowed"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {chassi && (
          <div className="mt-4">
            <Label className="text-sm font-medium">Documentos do Veículo</Label>
            <DocumentUpload
              sigla="VEI"
              registro={chassi || initial?.cve_chassi || ""}
            />
          </div>
        )}

        {errorMessage && (
          <div className="text-red-500 text-sm mt-2">{errorMessage}</div>
        )}

        <div className="pt-4 flex justify-end gap-4">
          <Button
            type="button"
            variant="border"
            size="small"
            onClick={onCancel}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button type="submit" size="small" disabled={saving}>
            {saving ? "Atualizando..." : "Atualizar"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
